import { useQuery } from '@apollo/client';
import { Col, Row, Tooltip } from 'antd';
import classNames from 'classnames';
import { isEqual } from 'lodash-es';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { formLocationToGraphqlInput } from '../../../app/data/newQuoteConversions';
import { CURRENT_TIME_QUERY } from '../../../app/graphql/geographicQueries';
import { NoLabelForm } from '../../../components/forms/forms';
import { FormItemSchemaDefinedSelect } from '../../../components/forms/selects';
import { ConfigurableCard } from '../../../components/layout/cardElements';
import { useLayoutElementsStyles } from '../../../components/layout/layoutElements.styles';
import AddressBookControls from '../../../dialogs/shipments/AddressBookControls';
import { cssVariables } from '../../../styles/cssVariables';
import { pxToNumber } from '../../../utils/cssUtils';
import {
  AddressLookupCard,
  NewShipmentBaseRow,
} from '../newShipment/newShipmentElements';
import { useNewShipmentElementsStyles } from '../newShipment/newShipmentElements.styles';
import { DangerousGoodsSection } from '../newShipment/NewShipmentServiceInformationForm';
import { useNewShipmentServiceInformationFormStyles } from '../newShipment/NewShipmentServiceInformationForm.styles';
import QuotePackageListPackages from '../packageList/QuotePackageList';
import { PickupDateTimeField } from '../shipmentFormsCommon/shipmentDateFields';
import { ServiceTypeField } from '../shipmentFormsCommon/shipmentServiceInformationFields';
import { ACCOUNT_INFO } from '../../../app/graphql/accountQueries';
import { useNewQuoteFormStyles } from './newQuoteForm.styles';

const QUOTE_MAX_FUTURE_DAYS = 30;
const DateCell = ({ datetime, disabled }) => {
  if (disabled)
    return (
      <Tooltip
        title={<FormattedMessage id="book.newQuote.dateRestrict" />}
        trigger="hover"
      >
        <div className="ant-picker-cell-inner">{datetime.date()}</div>
      </Tooltip>
    );
  return <div className="ant-picker-cell-inner">{datetime.date()}</div>;
};

const NewQuoteForm = ({
  name,
  className,
  form,
  formRef,
  onValuesChange,
  initialValues,
  onEditedPackageChange,
  onSubmit,
  account,
}) => {
  const { styles: newShipmentElementsStyles } = useNewShipmentElementsStyles();
  const { styles: serviceInformationStyles } =
    useNewShipmentServiceInformationFormStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const { styles } = useNewQuoteFormStyles(layoutStyles);
  const { data: accountInfoData } = useQuery(ACCOUNT_INFO, {
    variables: { accountNumber: account?.number },
    skip: !account,
  });
  const { dangerousGoodsQuote: dangerousGoods } =
    accountInfoData?.accountInfo || {};

  const [currentOrigin, setCurrentOrigin] = useState();

  const { data } = useQuery(CURRENT_TIME_QUERY, {
    variables: { address: currentOrigin },
    skip: !currentOrigin,
  });

  const onValuesChangedInner = (changedValues, allValues) => {
    const originAddress = formLocationToGraphqlInput(allValues.origin)?.address;
    if (!isEqual(originAddress, currentOrigin)) {
      setCurrentOrigin(originAddress);
    }
    onValuesChange && onValuesChange({ changedValues, allValues });
  };

  const currentDate = moment(data?.currentTime);
  const isDateDisabled = date =>
    date.diff(currentDate, 'day') > QUOTE_MAX_FUTURE_DAYS;

  useEffect(() => {
    if (initialValues?.pickupDateTime) {
      form.validateFields(['pickupDateTime']);
    }
  }, [form, initialValues?.pickupDateTime]);

  return (
    <NoLabelForm
      form={form}
      formRef={formRef}
      onValuesChange={onValuesChangedInner}
      name={name}
      className={className}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      <Row className={classNames('flex-nowrap', styles.root)}>
        <Col
          className={classNames(layoutStyles.flex1, 'spaces-vert-norm2')}
          lg={24}
        >
          <ConfigurableCard
            titleId="book.newShipment.serviceInformation"
            bodyComponent="div"
            bodyClassName={classNames(
              newShipmentElementsStyles.newShipmentCardBody,
              styles.newShipmentServiceInformationCard,
              serviceInformationStyles.newShipmentServiceInformationCard
            )}
          >
            <NewShipmentBaseRow>
              <Col md={12} xs={24}>
                <NewShipmentBaseRow>
                  <Col
                    lg={24}
                    className={classNames(
                      styles.jobLocationCardSection,
                      newShipmentElementsStyles.jobLocationCardSection
                    )}
                  >
                    <div
                      className={classNames(
                        styles.jobLocationCardSectionHeader,
                        newShipmentElementsStyles.jobLocationCardSectionHeader
                      )}
                    >
                      <div
                        className={classNames(
                          'JobLocationCard-SectionTitle',
                          newShipmentElementsStyles.jobLocationCardSectionTitle,
                          layoutStyles.flex1
                        )}
                      >
                        <FormattedMessage id="book.newQuote.serviceRequested" />
                      </div>
                    </div>
                  </Col>
                </NewShipmentBaseRow>
                <NewShipmentBaseRow>
                  <Col xs={12} className={styles.newQuoteCol}>
                    <ServiceTypeField />
                  </Col>
                  <Col xs={12} className={styles.newQuoteCol}>
                    <FormItemSchemaDefinedSelect
                      name="commodity"
                      schemaName="serviceInformation.commodity"
                      labelId="book.newShipment.label.commodity"
                      allowFreeText
                      textAsValue
                    />
                  </Col>
                </NewShipmentBaseRow>
              </Col>
              <Col xs={24} md={12}>
                <PickupDateTimeField
                  getAddress={({ formValues }) => formValues.origin?.address}
                  addressSchemaPrefix="origin"
                  validateHoliday={false}
                  dateInputProps={{
                    disabledDate: isDateDisabled,
                    cellRender: date => (
                      <DateCell
                        datetime={date}
                        disabled={isDateDisabled(date)}
                      />
                    ),
                  }}
                />
              </Col>
            </NewShipmentBaseRow>
          </ConfigurableCard>

          <NewShipmentBaseRow
            gutter={[
              pxToNumber(cssVariables.spaceNorm2),
              pxToNumber(cssVariables.spaceNorm2),
            ]}
          >
            <Col xs={24} xl={12}>
              <AddressLookupCard
                titleId="book.newShipment.fromAddress"
                schemaNamePrefix="origin"
                enableAddressLookup
                fieldName="addressOrigin"
                namePrefix="origin"
                showCountry
                countryCodeRestrict="US"
                showSpecialInstructions={false}
                renderHeaderExtra={type => (
                  <AddressBookControls
                    name="origin"
                    type={type}
                    namePrefix="origin"
                    countryCodeRestrict="US"
                  />
                )}
              />
            </Col>
            <Col xs={24} xl={12}>
              <AddressLookupCard
                titleId="book.newShipment.toAddress"
                schemaNamePrefix="destination"
                enableAddressLookup
                fieldName="addressDestination"
                namePrefix="destination"
                showCountry
                countryCodeRestrict="US"
                showSpecialInstructions={false}
                renderHeaderExtra={type => (
                  <AddressBookControls
                    name="destination"
                    type={type}
                    namePrefix="destination"
                    countryCodeRestrict="US"
                  />
                )}
              />
            </Col>
          </NewShipmentBaseRow>
          <NewShipmentBaseRow>
            <Col xs={24} xl={12}>
              <ConfigurableCard
                titleId="book.newShipment.serviceInformation"
                bodyComponent="div"
                bodyClassName={styles.newQuotePackagesCardBody}
              >
                <QuotePackageListPackages
                  onEditedPackageChange={onEditedPackageChange}
                  activeTemplateCounter
                />
              </ConfigurableCard>
            </Col>
            {dangerousGoods && (
              <Col xs={24} xl={12}>
                <ConfigurableCard
                  titleId="book.newShipment.dangerousGoodsDeclaration"
                  bodyComponent="div"
                  bodyClassName={classNames(
                    newShipmentElementsStyles.newShipmentCardBody,
                    styles.newShipmentServiceInformationCard,
                    styles.newQuoteDGCard,
                    serviceInformationStyles.newShipmentServiceInformationCard
                  )}
                >
                  <DangerousGoodsSection noDivider />
                </ConfigurableCard>
              </Col>
            )}
          </NewShipmentBaseRow>
        </Col>
      </Row>
    </NoLabelForm>
  );
};

export default NewQuoteForm;
